// Blip's own icons (his draw_icon tool, docs/BLIP_RECIPES_DESIGN.md): small
// pictures he draws himself, such as a recipe's tile icon, from the shapes
// the World Editor's Add palette icons are made of, and in their style:
// cartoon-shaded, outlined in the palette's dark blue, seen from its
// three-quarter view and fitted to a 128-pixel square with a clear
// background. The drawing is the palette's own
// (tools/world_editor_icons/add_object_icons.js, which esbuild bundles here),
// so his icons sit beside the editor's.
//
// Each is saved as a PNG that says it is one of his (a tEXt chunk), so that
// drawing it again replaces it, where any other file is kept (tools.js).

import "../../../../tools/world_editor_icons/add_object_icons.js";

export class IconError extends Error {}

export const ICON_SIZE = 128;
const MOST_SHAPES = 40;
// A shape's members, as tools.json describes them.
const MEMBERS = ["shape", "position", "rotation", "scale", "color", "edges", "layer", "seed"];
// The tEXt chunk his icons carry.
const MARK = "Software\0Blitz3D+ Blip draw_icon";

const finite = (value) => typeof value === "number" && Number.isFinite(value);

// Three numbers from low to high, or null when not given.
function triple(value, name, low, high, example) {
  if (value === undefined || value === null) return null;
  if (!Array.isArray(value) || value.length !== 3 || !value.every((each) => finite(each) && each >= low && each <= high)) {
    throw new IconError(`${name} is three numbers from ${low} to ${high}, such as ${example}.`);
  }
  return value.slice();
}

// Claude's shapes, checked, as add_object_icons.js draws them.
export function iconShapes(shapes) {
  const kinds = window.WorldEditorIcons.shapes;
  if (!Array.isArray(shapes) || !shapes.length || shapes.length > MOST_SHAPES) {
    throw new IconError(`shapes is a list of 1 to ${MOST_SHAPES} shapes, such as [{ "shape": "cube", "color": [0, 150, 255] }].`);
  }
  return shapes.map((shape, index) => {
    const at = `shapes[${index}]`;
    if (!shape || typeof shape !== "object" || Array.isArray(shape)) {
      throw new IconError(`${at} is a shape, such as { "shape": "cube", "position": [0, 1, 0] }.`);
    }
    const unknown = Object.keys(shape).find((key) => !MEMBERS.includes(key));
    if (unknown) throw new IconError(`${at} has ${unknown}, which a shape does not: its members are ${MEMBERS.join(", ")}.`);
    if (!kinds.includes(shape.shape)) throw new IconError(`${at}.shape is one of ${kinds.join(", ")}.`);
    const result = { shape: shape.shape };
    const position = triple(shape.position, `${at}.position`, -1000, 1000, "[0, 1, 0]");
    if (position) result.position = position;
    const rotation = triple(shape.rotation, `${at}.rotation`, -3600, 3600, "[0, 45, 0]");
    if (rotation) result.rotation = rotation;
    if (finite(shape.scale)) {
      if (shape.scale < 0.001 || shape.scale > 1000) throw new IconError(`${at}.scale is from 0.001 to 1000.`);
      result.scale = shape.scale;
    } else {
      const scale = triple(shape.scale, `${at}.scale`, 0.001, 1000, "[2, 0.2, 2], or a number");
      if (scale) result.scale = scale;
    }
    const color = triple(shape.color, `${at}.color`, 0, 255, "[0, 150, 255]");
    if (color) result.color = color.map(Math.round);
    if (shape.edges !== undefined) {
      if (typeof shape.edges !== "boolean") throw new IconError(`${at}.edges is true or false.`);
      result.edges = shape.edges;
    }
    if (shape.layer !== undefined) {
      if (!Number.isInteger(shape.layer) || Math.abs(shape.layer) > 10) throw new IconError(`${at}.layer is a whole number from -10 to 10.`);
      result.layer = shape.layer;
    }
    if (shape.seed !== undefined) {
      if (!Number.isInteger(shape.seed) || shape.seed < 1 || shape.seed > 1000000) throw new IconError(`${at}.seed is a whole number from 1.`);
      result.seed = shape.seed;
    }
    return result;
  });
}

// Claude's view, checked: { yaw, pitch }, the palette's where not given.
export function iconView(view) {
  if (view === undefined || view === null) return {};
  if (typeof view !== "object" || Array.isArray(view)) throw new IconError('view is { "yaw": 32, "pitch": 24 }, or leave it out.');
  const result = {};
  if (view.yaw !== undefined) {
    if (!finite(view.yaw) || Math.abs(view.yaw) > 360) throw new IconError("view.yaw is in degrees, from -360 to 360.");
    result.yaw = view.yaw;
  }
  if (view.pitch !== undefined) {
    if (!finite(view.pitch) || view.pitch < -45 || view.pitch > 90) throw new IconError("view.pitch is in degrees, from 0 (level) to 90 (from above).");
    result.pitch = view.pitch;
  }
  return result;
}

// The icon drawn, as a PNG's bytes that say it is his.
export async function drawIcon(shapes, view) {
  const canvas = document.createElement("canvas");
  canvas.width = canvas.height = ICON_SIZE;
  const context = canvas.getContext("2d");
  window.WorldEditorIcons.drawShapes(context, shapes, view, 0, 0, ICON_SIZE);
  const pixels = context.getImageData(0, 0, ICON_SIZE, ICON_SIZE).data;
  let shown = 0;
  for (let at = 3; at < pixels.length; at += 4) if (pixels[at] > 12) ++shown;
  if (!shown) throw new IconError("Nothing showed: every shape is too thin to see from the view. Give each some size every way.");
  const blob = await new Promise((resolve) => canvas.toBlob(resolve, "image/png"));
  return marked(new Uint8Array(await blob.arrayBuffer()));
}

const CRC = new Uint32Array(256).map((_, n) => {
  let crc = n;
  for (let bit = 0; bit < 8; ++bit) crc = crc & 1 ? 0xedb88320 ^ (crc >>> 1) : crc >>> 1;
  return crc;
});

function crc32(bytes) {
  let crc = 0xffffffff;
  for (const byte of bytes) crc = CRC[(crc ^ byte) & 255] ^ (crc >>> 8);
  return (crc ^ 0xffffffff) >>> 0;
}

// A PNG with the mark after its header (IHDR, which ends 33 bytes in).
function marked(png) {
  const text = new TextEncoder().encode(MARK);
  const chunk = new Uint8Array(12 + text.length), view = new DataView(chunk.buffer);
  view.setUint32(0, text.length);
  chunk.set([0x74, 0x45, 0x58, 0x74], 4);
  chunk.set(text, 8);
  view.setUint32(8 + text.length, crc32(chunk.subarray(4, 8 + text.length)));
  const result = new Uint8Array(png.length + chunk.length);
  result.set(png.subarray(0, 33));
  result.set(chunk, 33);
  result.set(png.subarray(33), 33 + chunk.length);
  return result;
}

// Whether a file is an icon of his: a PNG with the mark before its picture.
export function isBlipIcon(bytes) {
  if (!bytes || bytes.length < 8 || bytes[0] !== 0x89 || bytes[1] !== 0x50 || bytes[2] !== 0x4e || bytes[3] !== 0x47) return false;
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  for (let at = 8; at + 8 <= bytes.length;) {
    const length = view.getUint32(at), type = String.fromCharCode(...bytes.subarray(at + 4, at + 8));
    if (type === "tEXt" && length === MARK.length && at + 8 + length <= bytes.length &&
        String.fromCharCode(...bytes.subarray(at + 8, at + 8 + length)) === MARK) return true;
    if (type === "IDAT" || type === "IEND") return false;
    at += 12 + length;
  }
  return false;
}
